/**
 * Billing account note slice pieces — the free-text notes staff attach to a
 * billing account, newest first.
 *
 * Creating a note also links its id back onto the owning account's `notes`
 * list, so that reducer reaches into the accounts sub-state.
 *
 * Contains:
 * - accountNotesInitialState — adapter state (no view substate)
 * - addAccountNoteCases — edit / create / delete extra reducers
 * - note selectors, including the per-account newest-first list
 */
import { createSelector } from '@reduxjs/toolkit';

import {
  createBillingAccountNote,
  deleteBillingAccountNote,
  editBillingAccountNote,
} from '@sophocles/features/billing/api';
import { billingAccountNotesAdapter } from '@sophocles/features/billing/slices/adapters';
import { selectBillingAccount } from '@sophocles/features/billing/slices/billingAccounts';
import { memoizeByKey } from '@sophocles/utils/memoizeByKey';

export const accountNotesInitialState = billingAccountNotesAdapter.getInitialState();

export const addAccountNoteCases = builder => {
  builder
    .addCase(editBillingAccountNote.fulfilled, (state, action) => {
      billingAccountNotesAdapter.upsertOne(state.accountNotes, action.payload);
    })
    .addCase(createBillingAccountNote.fulfilled, (state, action) => {
      billingAccountNotesAdapter.upsertOne(state.accountNotes, action.payload);
      const { account } = action.payload;
      state.accounts.entities[account].notes.push(action.payload.id);
    })
    .addCase(deleteBillingAccountNote.fulfilled, (state, action) => {
      billingAccountNotesAdapter.removeOne(state.accountNotes, action.payload);
    });
};

export const selectBillingAccountNoteEntities = state => {
  return state.billing.accountNotes;
};
export const { selectById: selectBillingNoteById, selectIds: selectBillingNoteIds } =
  billingAccountNotesAdapter.getSelectors(selectBillingAccountNoteEntities);

export const selectBillingNotesForAccount = memoizeByKey(accountId =>
  createSelector(
    selectBillingAccount(accountId),
    state => state,
    (billingAccount, state) => {
      if (billingAccount && billingAccount.notes) {
        return billingAccount.notes
          .map(noteId => selectBillingNoteById(state, noteId))
          .filter(note => note != null)
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      }
      return [];
    },
  ),
);
